import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { AccordionSection, CardContent, CardIcon, H4, Icon, IconTypes, Section, Tag, Tooltip, TooltipIcon } from 'components';
import { Loadable } from 'components/types';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { centsToDollarsDisplay } from 'utils/currency';

export const ContentHeader = styled(Section)`
  padding-bottom: 0;

  > div {
    padding-bottom: 0;
    border-bottom: none;
  }
`;

export const SiteOverviewList = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  list-style: none;

  .site-item-header {
    background-color: ${theme.color.neutral[100]};
  }
`;

interface SiteOverviewTitleProps {
  address: string;
  title: string;
  customerSiteId?: string;
}

const TitleWrap = styled.span`
  position: relative;
  display: inline;
  word-break: break-word;
`;

const TitleSection = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const Title = styled.div``;

const CustomerSiteId = styled.div`
  margin-left: ${toRem(8)};
  font-size: ${toRem(12)};
`;

const AddressTooltip = styled(Tooltip)`
  flex-shrink: 0;
`;

const AddressTooltipIcon = styled(Icon)`
  position: relative;
  top: ${toRem(2)};
  margin-left: ${toRem(4)};
  fill: ${theme.color.neutral[200]};
  cursor: pointer;
  transition: fill ${theme.transition.icon};

  &:hover,
  &:focus {
    fill: ${theme.color.neutral[400]};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    display: none !important;
  }
`;

const SiteOverviewTitle = ({ address, title, customerSiteId }: SiteOverviewTitleProps) => {
  const getTooltipId = `tooltip-${title}`;

  return (
    <TitleWrap>
      <TitleSection>
        <Title>{title}</Title>
        <CustomerSiteId>{customerSiteId && `(${customerSiteId})`}</CustomerSiteId>
      </TitleSection>
      <AddressTooltipIcon label="Hover for address." icon="Site" width={13.75} tooltipId={getTooltipId} />
      <AddressTooltip id={getTooltipId} place="right">
        {address}
      </AddressTooltip>
    </TitleWrap>
  );
};

const SiteOverviewSubtitle = styled(H4)`
  display: none;

  @media (min-width: ${theme.breakpoint.med}px) {
    display: flex;
  }
`;

const PendingTag = styled(Tag).attrs({
  appearance: Appearance.WARNING,
  size: Size.SMALL,
})`
  flex-shrink: 0;
  min-width: ${toRem(115)};
  height: ${toRem(24.75)};
  padding: 0 ${toRem(8.75)};
  background-color: ${theme.color.warning.medium};
  font-weight: ${theme.font.weight.medium};
  text-align: center;
`;

const SiteOverviewContent = styled.ul`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(11)} ${toRem(11)};
  width: 100%;
  padding: ${toRem(11)} ${toRem(11)};
  list-style: none;

  @media (min-width: 870px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

interface SiteOverviewItemProps extends Loadable {
  ctaText: string;
  icon: IconTypes;
  iconBGColor: string;
  iconFill: string;
  label?: string;
  onClick?: () => void;
  tooltip?: ReactNode | string;
  value?: number | string;
}

// SOI = SiteOverviewItem
const SOILayout = styled.li`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: ${toRem(55)};
  padding: 0 ${toRem(11)};
`;

const StyledCardIcon = styled(CardIcon)`
  && {
    width: ${toRem(33)};
    height: ${toRem(33)};
    margin-right: ${toRem(11)};

    svg {
      width: ${toRem(16.5)};
      height: ${toRem(16.5)};
    }
  }
`;

const SOIDetailsLayout = styled.section`
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  height: 100%;
  color: ${theme.color.neutral[900]};
`;

const SOILabel = styled.label`
  margin-bottom: ${toRem(5.5)};
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(11)};
  line-height: 100%;
`;

const SOIValue = styled.span`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(13.25)};
  line-height: 100%;
`;

const SOIAction = styled.button`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.medium};
  font-size: ${toRem(11)};
  line-height: 100%;
  color: ${theme.color.neutral[800]};
  transition: color ${theme.transition.button.fast};

  svg {
    margin-left: ${toRem(8.75)};
    fill: ${theme.color.neutral[800]};
    transition: fill ${theme.transition.button.fast};
  }

  &:hover {
    color: ${theme.color.neutral[900]};

    svg {
      fill: ${theme.color.neutral[900]};
    }
  }
`;

const SiteOverviewItem = ({ ctaText, icon, iconBGColor, iconFill, isLoading, label, onClick, tooltip, value }: SiteOverviewItemProps) => (
  <SOILayout>
    <SOIDetailsLayout>
      <StyledCardIcon icon={icon} fill={iconFill} bgColor={iconBGColor} isLoading={isLoading} />
      <CardContent>
        <SOILabel>{isLoading ? <Skeleton width={75} /> : label}</SOILabel>
        <SOIValue>
          {isLoading ? (
            <Skeleton width={50} />
          ) : (
            <>
              {value}
              {tooltip && (
                <TooltipIcon id={label} iconSize={13.75} iconMarginLeft={3.75}>
                  {tooltip}
                </TooltipIcon>
              )}
            </>
          )}
        </SOIValue>
      </CardContent>
    </SOIDetailsLayout>

    {!isLoading && (
      <SOIAction onClick={onClick} data-cy={`site-action-${label}`.toLocaleLowerCase().replace(' ', '-')}>
        {`${ctaText} `} <Icon icon="Next" width={7} height={11} />
      </SOIAction>
    )}
  </SOILayout>
);

interface SiteDashboardItemProps extends Loadable {
  id: string;
  name: string;
  address: string;
  customerSiteId?: string;
  pendingRechargeBalance?: number;
  pendingRechargeDate?: string;
  pendingFunds: number;
  goToActiveEmployees?: () => void;
  goToPaymentsOverview?: () => void;
  goToPaymentsDetail?: () => void;
  goToPendingInvitations?: () => void;
  retainer: number;
  accountBalance: number;
  sevenDayTotal: number;
  expiredPayments: number;
  totalUsers: number;
  usersRequiringApproval: number;
}

export const SiteDashboardItem = ({
  isLoading,
  id,
  name,
  address,
  customerSiteId,
  pendingRechargeDate,
  pendingRechargeBalance,
  pendingFunds,
  goToActiveEmployees,
  goToPaymentsOverview,
  goToPaymentsDetail,
  goToPendingInvitations,
  retainer,
  accountBalance,
  sevenDayTotal,
  expiredPayments,
  totalUsers,
  usersRequiringApproval,
}: SiteDashboardItemProps) => (
  <AccordionSection
    className="site-item"
    actions={
      pendingRechargeBalance && pendingRechargeDate && !isLoading ? (
        <PendingTag
          label="Pending Recharge"
          tooltipId={`${name}-${id}-pending-recharge`}
          tooltipMessage={
            <p>
              <strong>Amount:</strong> {centsToDollarsDisplay(pendingFunds)}
            </p>
          }
          tooltipPlace="left"
        />
      ) : null
    }
    as="li"
    hideContentOverflow
    isLoading={isLoading}
    title={<SiteOverviewTitle title={name} address={address} customerSiteId={customerSiteId} />}
    subtitle={<SiteOverviewSubtitle>{address}</SiteOverviewSubtitle>}
  >
    <SiteOverviewContent>
      <SiteOverviewItem
        ctaText="Review Payments"
        icon="Wallet"
        iconBGColor={theme.color.neutral[100]}
        iconFill={theme.color.neutral[400]}
        isLoading={isLoading}
        label="Account Balance"
        onClick={goToPaymentsOverview}
        value={centsToDollarsDisplay(accountBalance)}
      />
      <SiteOverviewItem
        ctaText="Review Payments"
        icon="Wallet"
        iconBGColor={theme.color.neutral[100]}
        iconFill={theme.color.neutral[400]}
        isLoading={isLoading}
        label="Retainer"
        onClick={goToPaymentsOverview}
        value={centsToDollarsDisplay(retainer)}
      />
      <SiteOverviewItem
        ctaText="Review Payments"
        icon="Payment"
        iconBGColor={theme.color.success.light}
        iconFill={theme.color.success.medium}
        isLoading={isLoading}
        label="7 Day Total"
        onClick={goToPaymentsOverview}
        value={centsToDollarsDisplay(sevenDayTotal)}
      />
      <SiteOverviewItem
        ctaText="View Payments"
        icon="Payment"
        iconBGColor={theme.color.danger.light}
        iconFill={theme.color.danger.medium}
        isLoading={isLoading}
        label="Expired Payments"
        onClick={goToPaymentsDetail}
        value={expiredPayments}
      />
      <SiteOverviewItem ctaText="Manage Users" icon="People" iconBGColor={theme.color.neutral[100]} iconFill={theme.color.neutral[400]} isLoading={isLoading} label="Total Users" onClick={goToActiveEmployees} value={totalUsers} />
      <SiteOverviewItem
        ctaText="Review Users"
        icon="Person"
        iconBGColor={theme.color.primary[25]}
        iconFill={theme.color.primary[500]}
        isLoading={isLoading}
        label="Users Requiring Approval"
        onClick={goToPendingInvitations}
        value={usersRequiringApproval}
      />
    </SiteOverviewContent>
  </AccordionSection>
);
